/**
 * KlantMarketingTab — marketing-historie en -acties op klant-detail.
 *
 * Toont per klant (op sf_account_id): zendhistorie met events, journey-status,
 * subscription per merk en suppressies. Acties (admin-only):
 *  - "Heeft gereageerd op …" → campagne/journey-gescopete suppressie
 *  - "Uitschrijven voor alle marketing" → global suppressie + unsubscribe
 *  - "Verwijder uit journey" → state exited_manual
 */
import { useCallback, useEffect, useState } from 'react';
import { marketingFetch } from '../../stores/marketing-store';
import { useUserRole } from '../../hooks/useUserRole';
import { StatusBadge, SUPPRESSION_REASON_LABELS, formatDateTime } from '../../pages/marketing/shared';

interface CustomerMarketing {
  contact: {
    id: string;
    email: string;
    recency: string | null;
    last_service_date: string | null;
    order_count: number;
  } | null;
  subscriptions: Array<{ id: string; brand: string; status: string; source: string; changed_at: string }>;
  suppressions: Array<{
    id: string;
    scope: string;
    reason: string;
    campaign_id: string | null;
    journey_id: string | null;
    created_at: string;
    marketing_campaigns?: { name: string } | null;
    marketing_journeys?: { name: string } | null;
  }>;
  journeyStates: Array<{
    id: string;
    journey_id: string;
    status: string;
    current_step: number;
    next_action_at: string | null;
    entered_at: string;
    marketing_journeys?: { id: string; key: string; name: string; status: string } | null;
  }>;
  sends: Array<{
    id: string;
    subject: string | null;
    status: string;
    sent_at: string | null;
    queued_at: string;
    campaign_id: string | null;
    journey_id: string | null;
    marketing_campaigns?: { id: string; name: string } | null;
    marketing_journeys?: { id: string; name: string } | null;
    marketing_templates?: { key: string; name: string } | null;
    events: Array<{ event_type: string; occurred_at: string }>;
  }>;
}

export function KlantMarketingTab({
  sfAccountId,
  onOpenMail,
}: {
  sfAccountId: string;
  /** Opent de verstuurde mail in de tijdlijn-drawer. */
  onOpenMail?: (send: { id: string; subject: string | null; via: string | null; status: string; date: string | null }) => void;
}) {
  const { role } = useUserRole();
  const isAdmin = role === 'admin';
  const [data, setData] = useState<CustomerMarketing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [repliedTarget, setRepliedTarget] = useState('');
  const [expandedSend, setExpandedSend] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await marketingFetch<CustomerMarketing>(`/api/marketing/customer?sfId=${sfAccountId}`);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  }, [sfAccountId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <div className="p-4 text-sm text-red-600">{error}</div>;
  if (!data) return <div className="p-4 text-sm text-slate-400">Laden…</div>;
  if (!data.contact) {
    return (
      <div className="p-4 text-sm text-slate-400">
        Geen marketing-contact voor deze klant — het bestand wordt nachtelijks gesynct uit Salesforce (Kleda-klanten met
        e-mailadres).
      </div>
    );
  }

  const contact = data.contact;
  const activeJourneys = data.journeyStates.filter((s) => s.status === 'active');

  // Doelen voor "Heeft gereageerd op …": recente campagnes/journeys uit de zendhistorie.
  const repliedTargets = new Map<string, { label: string; scope: 'campaign' | 'journey'; id: string }>();
  for (const send of data.sends) {
    if (send.campaign_id && send.marketing_campaigns) {
      repliedTargets.set(`campaign:${send.campaign_id}`, {
        label: `Campagne: ${send.marketing_campaigns.name}`,
        scope: 'campaign',
        id: send.campaign_id,
      });
    }
    if (send.journey_id && send.marketing_journeys) {
      repliedTargets.set(`journey:${send.journey_id}`, {
        label: `Journey: ${send.marketing_journeys.name}`,
        scope: 'journey',
        id: send.journey_id,
      });
    }
  }

  const markReplied = async () => {
    const target = repliedTargets.get(repliedTarget);
    if (!target) return;
    setBusy(true);
    try {
      await marketingFetch('/api/marketing/suppressions', {
        method: 'POST',
        body: {
          contact_id: contact.id,
          scope: target.scope,
          campaign_id: target.scope === 'campaign' ? target.id : undefined,
          journey_id: target.scope === 'journey' ? target.id : undefined,
          reason: 'replied_manual',
        },
      });
      setRepliedTarget('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Actie mislukt');
    } finally {
      setBusy(false);
    }
  };

  const unsubscribeAll = async () => {
    if (!window.confirm(`${contact.email} uitschrijven voor alle marketing? Dit wordt teruggesynct naar Salesforce.`)) return;
    setBusy(true);
    try {
      await marketingFetch('/api/marketing/suppressions', {
        method: 'POST',
        body: { contact_id: contact.id, scope: 'global', reason: 'manual' },
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Actie mislukt');
    } finally {
      setBusy(false);
    }
  };

  const removeFromJourney = async (journeyId: string, journeyName: string) => {
    if (!window.confirm(`Klant verwijderen uit journey "${journeyName}"?`)) return;
    setBusy(true);
    try {
      await marketingFetch(`/api/marketing/journeys?id=${journeyId}&action=remove-contact`, {
        method: 'POST',
        body: { contact_id: contact.id },
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Actie mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 space-y-5">
      {/* Subscription-status */}
      <div>
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Inschrijvingen</h4>
        {data.subscriptions.length === 0 ? (
          <p className="text-sm text-slate-400">Geen subscription-rijen.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.subscriptions.map((sub) => (
              <span
                key={sub.id}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  sub.status === 'subscribed' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'
                }`}
              >
                <span className="capitalize">{sub.brand}</span>
                <span>·</span>
                {sub.status === 'subscribed' ? 'ingeschreven' : 'uitgeschreven'}
              </span>
            ))}
          </div>
        )}
        {isAdmin && data.subscriptions.some((s) => s.status === 'subscribed') && (
          <button
            onClick={unsubscribeAll}
            disabled={busy}
            className="mt-2 px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
          >
            Uitschrijven voor alle marketing
          </button>
        )}
      </div>

      {/* Journeys */}
      {(activeJourneys.length > 0 || data.journeyStates.length > 0) && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Journeys</h4>
          <div className="space-y-2">
            {data.journeyStates.map((state) => (
              <div key={state.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-slate-900">{state.marketing_journeys?.name ?? 'Journey'}</p>
                  <p className="text-xs text-slate-400">
                    Stap {state.current_step + 1}
                    {state.next_action_at ? ` · volgende actie ${formatDateTime(state.next_action_at)}` : ''} · sinds{' '}
                    {formatDateTime(state.entered_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusBadge status={state.status} />
                  {isAdmin && state.status === 'active' && (
                    <button
                      onClick={() => removeFromJourney(state.journey_id, state.marketing_journeys?.name ?? 'journey')}
                      disabled={busy}
                      className="text-xs text-red-600 hover:underline disabled:opacity-50"
                    >
                      Verwijder
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suppressies + "heeft gereageerd" */}
      <div>
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Suppressies</h4>
        {data.suppressions.length === 0 ? (
          <p className="text-sm text-slate-400">Geen actieve suppressies.</p>
        ) : (
          <ul className="space-y-1">
            {data.suppressions.map((sup) => (
              <li key={sup.id} className="text-sm text-slate-600">
                {SUPPRESSION_REASON_LABELS[sup.reason] ?? sup.reason}
                {sup.scope !== 'global' && (
                  <span className="text-slate-400">
                    {' '}
                    — {sup.marketing_campaigns?.name ?? sup.marketing_journeys?.name ?? sup.scope}
                  </span>
                )}
                <span className="text-xs text-slate-400"> · {formatDateTime(sup.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
        {isAdmin && repliedTargets.size > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <select
              value={repliedTarget}
              onChange={(e) => setRepliedTarget(e.target.value)}
              className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white"
            >
              <option value="">Heeft gereageerd op…</option>
              {[...repliedTargets.entries()].map(([key, t]) => (
                <option key={key} value={key}>
                  {t.label}
                </option>
              ))}
            </select>
            <button
              onClick={markReplied}
              disabled={busy || !repliedTarget}
              className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              Stop follow-up
            </button>
          </div>
        )}
      </div>

      {/* Zendhistorie */}
      <div>
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Zendhistorie</h4>
        {data.sends.length === 0 ? (
          <p className="text-sm text-slate-400">Nog geen marketing-mails verstuurd naar deze klant.</p>
        ) : (
          <div className="space-y-1">
            {data.sends.map((send) => (
              <div key={send.id} className="bg-white border border-slate-200 rounded-lg">
                <button
                  onClick={() => setExpandedSend(expandedSend === send.id ? null : send.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-slate-900 truncate">{send.subject ?? send.marketing_templates?.name ?? '—'}</p>
                    <p className="text-xs text-slate-400">
                      {send.marketing_campaigns?.name ?? send.marketing_journeys?.name ?? 'los'} ·{' '}
                      {formatDateTime(send.sent_at ?? send.queued_at)}
                    </p>
                  </div>
                  <StatusBadge status={send.status} />
                </button>
                {expandedSend === send.id && (
                  <div className="px-3 pb-2 border-t border-slate-100 pt-2 space-y-2">
                    {send.events.length > 0 && (
                      <ul className="space-y-0.5">
                        {send.events.map((e, i) => (
                          <li key={i} className="text-xs text-slate-500">
                            {e.event_type} · {formatDateTime(e.occurred_at)}
                          </li>
                        ))}
                      </ul>
                    )}
                    {onOpenMail && (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenMail({
                            id: send.id,
                            subject: send.subject ?? send.marketing_templates?.name ?? null,
                            via: send.marketing_campaigns?.name ?? send.marketing_journeys?.name ?? null,
                            status: send.status,
                            date: send.sent_at ?? send.queued_at ?? null,
                          })
                        }
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        Bekijk verstuurde mail →
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
